/* istanbul ignore file */
import styled from 'styled-components';
import { Select, SelectButton, SelectOptions } from '@econsult/econsult-ui/dist/components/Select';
import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { InputMessage, InputMessageWrapper } from '@econsult/econsult-ui/dist/components/Input/InputStyles';
import { Paragraph } from '@econsult/econsult-ui/dist/components/shared/typography';
import { FlexColumnDiv, FlexDiv } from 'components/Div/styles';
import { InfoFilledIcon } from '@econsult/econsult-ui/dist/assets';
import { rgba } from 'polished';

export const ContentFieldsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.25rem;
`;

export const CharacterCounter = styled(Paragraph).attrs({ typography: 'ParagraphTinyHeavy' })`
  margin: 0;
  color: ${({ theme }) => theme.colorNeutralNeutral3};
  text-align: right;
  align-self: center;
  justify-self: end;
  width: 100%;
`;

export const FormLabelContainer = styled(FlexDiv)`
  position: relative;
  align-items: center;
`;

export const PatientWarning = styled(FlexDiv)`
  position: relative;
  margin-left: 8px;
  height: 16px;
  width: 16px;
`;

export const FormItemWrapper = styled(FlexColumnDiv)`
  position: relative;
  gap: 0.125rem;
`;

export const FormItemLabel = styled.span.attrs({
  typography: 'ParagraphTinyHeavy',
})`
  grid-column: 1 / -1;
  ${({ theme }) => theme.fontStyles};
  color: ${({ theme }) => rgba(theme.colorNeutralBlack, 0.8)};
`;

export const StyledSelect = styled(Select).attrs({
  theme: 'light',
  closeOnClickOutside: true,
})`
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'initial')};
  opacity: ${({ isDisabled }) => (isDisabled ? '0.5' : '1')};
`;

export const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledSelectButton = styled(SelectButton).attrs({ typography: 'ParagraphTinyRegular' })`
margin: 0;
padding: 0.75rem 1rem;
width: 100%;
border: 1px solid ${({ theme }) => theme.colorNeutralNeutral7};
border-radius: 0.5rem;
color: ${({ theme, isFilled, isDisabled }) => ((isFilled && !isDisabled) ? theme.colorNeutralBlack : theme.colorNeutralNeutral7)};
justify-content: space-between;
line-height: 1.75;
`;

export const StyledDeselectButton = styled(Button).attrs({
  fillStyle: 'borderless',
  size: 'small',
})`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.125rem;
  border: none !important;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const StyledSelectOptions = styled(SelectOptions)`
top: 100%;
width: 100%;
border-radius: 0;
`;

export const ErrorMessageWrapper = styled(InputMessageWrapper)`

  --input-color: ${({ theme }) => theme.colorStatusError};
  color: ${({ theme }) => theme.colorNeutralBrightWhite};
  padding: 0.375rem 0.5rem;
`;

export const ErrorMessage = styled(InputMessage).attrs({ typography: 'ParagraphTinyRegular' })``;

export const RadioLegend = styled(FormItemLabel).attrs({ as: 'legend' })``;

export const RadioWrapper = styled.div`
  && {
    display: grid;
    grid-template-columns: auto auto 1fr;
    row-gap: 0.25rem;
    column-gap: 2rem;
    align-items: center;

    & ${FormItemLabel} {
      grid-column: initial;
    }

    & > ${RadioLegend} {
      grid-column: 1;
    }

    p {
      grid-column: 1 / -1;
    }

    ${ErrorMessageWrapper} {
      padding: 0 10px;

      p {
        font-size: 10px;
        line-height: 19.2px;
      }
    }
  }
`;

export const RadioLabel = styled(FormItemLabel).attrs({ as: 'label' })`` as React.FC<
  React.LabelHTMLAttributes<HTMLLabelElement> & React.ComponentProps<typeof FormItemLabel>
>;
export const RadioInput = styled.input.attrs({ type: 'radio' })`
  accent-color: ${({ theme }) => theme.colorNeutralBlack};
`;

export const TemplateTypeInfo = styled(Paragraph).attrs({ typography: 'ParagraphTinyRegular' })`
  padding: 10px 12px;
  text-align: center;
  background: ${({ theme }) => theme.colorSecondarySecondary30};
  border: 1px solid ${({ theme }) => theme.colorSecondarySecondary60};
  border-radius: 8px;
  color: ${({ theme }) => theme.colorPrimaryPrimary100};
`;

export const TextAreaWrapper = styled.label`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 0.25rem;

  &:focus-within {
    ${FormItemLabel} {
      color: ${({ theme }) => theme.colorActionSecondaryActive};
    }
  }
`;

const TextAreaMessage = styled(Paragraph).attrs({ typography: 'ParagraphTinyHeavy' })`
  color: ${({ theme }) => theme.colorStatusError};
  margin: 0 0 -18px;
  display: flex;
  align-self: flex-end;
  align-items: center;
  svg {
    height: 10px;
    width: 10px;
    margin-left: 3px;
  }
`;

export const Validation = styled(TextAreaMessage)`
  color: ${({ theme }) => theme.colorStatusError};
`;

export const WarningIcon = styled(InfoFilledIcon)`
  && {
    height: 16px;
    width: 16px;
    margin: 0;
  }
  color: ${({ theme }) => theme.colorStatusCaution};
`;

export const TextArea = styled.textarea.attrs({ typography: 'ParagraphTinyRegular' })`

  --input-color: ${({ theme }) => theme.colorNeutralNeutral7};
  --outline-width: 1px;

  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  grid-column: 1 / -1;
  padding: 0.75rem 1rem;
  min-width: 100%;
  max-width: 100%;
  min-height: 10rem;
  max-height: 25vh;
  width: 100%;
  ${({ theme }) => theme.fontStyles};
  font-weight: 400;
  color: ${({ theme }) => theme.colorNeutralBlack};
  scrollbar-gutter: stable;
  outline: none;
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0 0 var(--outline-width) var(--input-color);

  &:focus {
    --input-color: ${({ theme }) => theme.colorActionSecondaryActive};
    --outline-width: 2px;
  }

  &::placeholder {
    font-weight: 400;
    color: ${({ theme }) => rgba(theme.colorNeutralBlack, 0.5)};
  }
`;
